import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { DEMO_MODE } from '@/lib/demoMode';

const HEARTBEAT_MS = 60_000;

/**
 * Marks the signed-in user "seen" on a throttled heartbeat while the tab is
 * visible, so a booking partner can see they are around (see get_presence).
 *
 * Deliberately cheap and best-effort: it only beats while the document is
 * visible (a backgrounded tab should not read as online), stamps once on
 * becoming visible, and every 60s thereafter. Errors are swallowed — presence
 * is a nicety, never something that should surface to the user.
 *
 * Mounted once, app-wide.
 */
export function usePresenceHeartbeat() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || DEMO_MODE) return;

    let timer: ReturnType<typeof setInterval> | undefined;

    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      // New RPC, not yet in the generated Functions map; cast like the other
      // new-RPC callers. Fire-and-forget.
      (supabase.rpc as unknown as (fn: string) => PromiseLike<unknown>)('touch_last_seen')
        .then(undefined, () => {});
    };

    const start = () => {
      beat();
      if (!timer) timer = setInterval(beat, HEARTBEAT_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = undefined; }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [user]);
}
